import React, {useState, useEffect, useContext, useRef} from 'react';
import {useRouter} from 'next/router';
import BookingService from '../../services/booking.service';
import UserService from '../../services/user.service';
import TNHContext from '@contexts/TNHContext';
import {getLocaleContent} from '@helper/locale-content.js';
import {useMasterDataStore} from '@stores/useMasterDataStore';
import {FormField, FormErrors} from '../Form';
import BookingSuccess from '../Home/BookingSuccess';
import ExpertiseService from '@services/expertise.service';
import {useBooking} from '@contexts/BookingContext';
import moment from 'moment';
import GoogleMapView from '@components/common/GoogleMap/GoogleMapPopup';

import {
    validateBookingForm,
    mapBookingDataToForm,
    createBookingSignature,
    hasBookingData,
    formatPhoneInput,
} from '@helper/booking-form.helper.js';

/**
 * Component form đặt lịch khám cho trang cơ sở y tế
 * Hospital cố định: không clear hospital khi click từ dịch vụ
 */
export default function FormRegister({hospitals = []}) {
    const {companyId} = useContext(TNHContext);
    const router = useRouter();
    const locale = router.locale || 'vn';
    const {bookingData, clearBookingData} = useBooking();

    const [selectedMapUrl, setSelectedMapUrl] = useState(null);
    const [open, setOpen] = useState(false);

    // Lấy dữ liệu master từ store
    const businessData = useMasterDataStore(s => s.businessData);
    const departments = useMasterDataStore(s => s.departments);
    const userOptions = useMasterDataStore(s => s.userOptions);
    const fetchAllMasterData = useMasterDataStore(s => s.fetchAllMasterData);
    const [expertises, setExpertises] = useState([]);

    const hospitalsWithMap = hospitals.map(b => {
        if (b.business_name.includes('Thái Nguyên') || b.business_name.includes('Thai Nguyen')) {
            return {
                ...b,
                mapUrl: 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7419.92591351833!2d105.82976!3d21.587369!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xc059ef64cbc6b293!2zQuG7h25oIFZp4buHbiBRdcO0zIFjIFTDqsyBIFRoYcyBaSBOZ3V5w6pu!5e0!3m2!1svi!2sus!4v1635393862311!5m2!1svi!2sus',
            };
        } else if (b.business_name.includes('Phổ Yên') || b.business_name.includes('Pho Yen')) {
            return {
                ...b,
                mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d29716.751769817314!2d105.86719314840859!3d21.405886272490253!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31351fe9a02a465d%3A0x861cba2d357f12c6!2zQuG7h25oIFZp4buHbiDEkGEgS2hvYSBZw6puIELDrG5oIFRow6FpIE5ndXnDqm4!5e0!3m2!1svi!2s!4v1686104129648!5m2!1svi!2s',
            };
        } else if (b.business_name.includes('Việt Yên') || b.business_name.includes('Viet Yen')) {
            return {
                ...b,
                mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.113232067809!2d106.10101907424647!3d21.26698497955583!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x313513ef19bf8947%3A0x3a66fac7f166c8a!2zQuG7h25oIHZp4buHbiBUTkggVmnhu4d0IFnDqm4!5e0!3m2!1svi!2s!4v1734595877069!5m2!1svi!2s',
            };
        } else if (b.business_name.includes('Lạng Sơn') || b.business_name.includes('Lang Son')) {
            return {
                ...b,
                mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2919.859095899616!2d106.74893180722017!3d21.847053483705068!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x36b54f0021135177%3A0x25c2c442cd3a15b0!2zQuG7h25oIHZp4buHbiBUTkggTOG6oW5nIFPGoW4!5e0!3m2!1svi!2s!4v1780049383996!5m2!1svi!2s',
            };
        }
        return b;
    });

    const [form, setForm] = useState({
        name: '',
        phone: '',
        email: '',
        hospital: '',
        department: '',
        doctor: '',
        department_name: '',
        expertises: '',
        expertises_name: '',
        doctor_name: '',
        time: '',
        symptoms: '',
    });

    const [doctors, setDoctors] = useState([]); // Danh sách bác sĩ được load theo chuyên khoa
    const [showSuccess, setShowSuccess] = useState(false);
    const [bookingCode, setBookingCode] = useState('');
    const [errors, setErrors] = useState({});
    const previousBookingSignature = useRef(null);
    const [isFromDoctorSection, setIsFromDoctorSection] = useState(false); // Đánh dấu thông tin đến từ section bác sĩ

    // Fetch master data khi component mount
    useEffect(() => {
        if (!locale) return;
        fetchAllMasterData(locale, companyId);
        const expertiseService = new ExpertiseService();
        expertiseService
            .getListHome({locale: locale})
            .then(response => {
                setExpertises(response);
                console.log(response);
            })
            .catch(error => {
                console.error('Error fetching expertise data:', error);
            });
    }, [locale, companyId, fetchAllMasterData]);

    // Load bác sĩ theo chuyên khoa khi department thay đổi
    useEffect(() => {
        if (!form.hospital) {
            setDoctors([]);
            return;
        }

        console.log('[FormRegister] Load bác sĩ theo chuyên khoa:', form.department);
        const userService = new UserService();
        userService
            .getUserOptions({
                businessId: form.hospital,
                locale,
                departmentId: form.department,
                expertiseId: form.expertises,
            })
            .then(response => {
                const doctorList = response?.data || response || [];
                console.log('[FormRegister] Đã load bác sĩ:', doctorList.length, 'bác sĩ');
                setDoctors(doctorList);
            })
            .catch(error => {
                console.error('[FormRegister] Lỗi khi load bác sĩ:', error);
                setDoctors([]);
            });
    }, [locale, form.department, form.hospital, form.expertises]);

    // Auto-fill form từ booking context
    useEffect(() => {
        if (!hasBookingData(bookingData)) return;

        const currentSignature = createBookingSignature(bookingData);
        if (previousBookingSignature.current === currentSignature) return;

        previousBookingSignature.current = currentSignature;

        const masterData = {businessData, departments, userOptions: doctors.length > 0 ? doctors : userOptions};
        const formValues = mapBookingDataToForm(bookingData, masterData, false); // false = không xóa hospital khi từ dịch vụ

        // Nếu có user_id từ booking data, ưu tiên dùng user_id
        if (bookingData.user_id) {
            formValues.doctor = String(bookingData.user_id);
        }

        // Đánh dấu thông tin đến từ section bác sĩ nếu có đủ 3 thông tin
        const hasAllInfo = !!(formValues.hospital && formValues.department && formValues.doctor);
        if (hasAllInfo) {
            setIsFromDoctorSection(true);
            console.log('[FormRegister] Thông tin đến từ section bác sĩ, sẽ disable 3 fields');
        }

        setForm(prev => ({
            ...prev,
            ...formValues,
            // Nếu có business_id trong booking data, hospital sẽ được tự động fill
        }));

        // Xóa booking data sau khi đã fill vào form
        setTimeout(() => {
            clearBookingData();
            previousBookingSignature.current = null;
        }, 500);
    }, [bookingData, businessData, departments, doctors, userOptions, clearBookingData]);

    // Sử dụng doctors đã load theo chuyên khoa, fallback về userOptions nếu chưa có
    const availableDoctors =
        doctors.length > 0
            ? doctors
            : form.department && userOptions.some(doctor => doctor.department_id)
              ? userOptions.filter(doctor => String(doctor.department_id) === String(form.department))
              : [];

    // Check xem có đủ 3 thông tin (hospital, department, doctor) và đến từ section bác sĩ để disable các fields
    const hasAllDoctorInfo = isFromDoctorSection && !!(form.hospital && form.department && form.doctor);

    // Xử lý thay đổi input trong form
    const handleChange = e => {
        const {name, value} = e.target;
        const text = e.target.selectedOptions?.[0]?.text || '';
        if (name === 'phone') {
            const digits = value.replace(/\D/g, '').slice(0, 11); // keep only numbers, max 11
            setForm(prev => ({...prev, phone: digits}));
            return;
        }

        // Nếu thay đổi department, clear doctor để tránh chọn bác sĩ không thuộc chuyên khoa mới
        if (name === 'department') {
            console.log('Thay đổi khoa ' + value + text);
            setForm(prev => ({
                ...prev,
                department: value,
                department_name: text,
                doctor: '', // Clear doctor khi đổi chuyên khoa
                expertise: '',
                doctor_name: '',
            }));
            // Reset flag khi user thay đổi department
            setIsFromDoctorSection(false);
        }
        if (name === 'expertises') {
            console.log('Thay đổi chuyên khoa ' + value + text);
            setForm(prev => ({
                ...prev,
                expertises: value,
                expertises_name: text,
                doctor: '', // Clear doctor khi đổi chuyên khoa
                doctor_name: '',
            }));
            // Reset flag khi user thay đổi department
            setIsFromDoctorSection(false);
        } else if (name === 'doctor') {
            // Reset flag khi user thay đổi doctor
            setIsFromDoctorSection(false);
            setForm(prev => ({...prev, doctor: value, doctor_name: text}));
        } else {
            setForm(prev => ({...prev, [name]: value}));
        }
        // Xóa lỗi khi user nhập lại
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    // Ngăn chặn nhập ký tự không phải số trong trường số điện thoại
    const handlePhoneKeyDown = e => {
        const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Escape', 'Enter'];
        if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
        if (!/[0-9]/.test(e.key)) e.preventDefault();
    };

    // Ngăn chặn gõ thủ công trong trường datetime
    const handleTimeKeyDown = e => {
        if (e.key === 'Tab') return;
        e.preventDefault();
    };

    // Hiển thị datetime picker
    const handleTimePicker = e => {
        try {
            e.target.showPicker?.();
        } catch (err) {
            console.warn('showPicker not supported:', err);
        }
    };

    // Xử lý submit form
    const handleSubmit = async e => {
        e.preventDefault();
        e.stopPropagation();

        // Kiểm tra tính hợp lệ của form
        const {errors: validationErrors, isValid} = validateBookingForm(form, locale);

        if (!isValid) {
            setErrors(validationErrors);
            return;
        }

        // Chuẩn bị dữ liệu booking
        const selectedBusiness = businessData.find(b => b.business_id === form.hospital);
        const bookingPayload = {
            ...form,
            business_code: selectedBusiness?.business_code || '',
            time: `${form.time}:00`,
        };

        // Gửi yêu cầu đặt lịch
        try {
            const bookingService = new BookingService();
            const response = await bookingService.makeABooking(bookingPayload);

            if (response.code === 0) {
                setBookingCode(response?.Extent?.MaDK || '');
                setShowSuccess(true);
                setForm({
                    name: '',
                    phone: '',
                    hospital: '',
                    department: '',
                    doctor: '',
                    expertises: '',
                    expertises_name: '',
                    time: '',
                    symptoms: '',
                });
            } else {
                alert(error);
            }
        } catch (error) {
            alert(error);
        }
    };

    return (
        <div className="register-section">
            <img src="/bw_image/bg-appointment-register.png" alt="bg" className="bg-img" />
            <div className="register-card">
                <div className="form-area">
                    <h2>{getLocaleContent(locale, 'page.booking-form.register-now-title')}</h2>
                    <form onSubmit={handleSubmit} noValidate>
                        {/* Hiển thị tất cả validation errors */}
                        <FormErrors errors={errors} />
                        <div className="form-grid">
                            <div>
                                <label>
                                    {getLocaleContent(locale, 'page.booking-form.full-name')} <span>*</span>
                                </label>
                                <FormField error={errors.name} preserveStyle>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder={getLocaleContent(
                                            locale,
                                            'page.booking-form.full-name-placeholder',
                                        )}
                                        style={{
                                            ...(errors.name ? {borderColor: '#ff4d4f'} : {}),
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                        }}
                                    />
                                </FormField>
                            </div>
                            <div>
                                <label>
                                    {getLocaleContent(locale, 'page.booking-form.phone')} <span>*</span>
                                </label>
                                <FormField error={errors.phone} preserveStyle>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        onKeyDown={handlePhoneKeyDown}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={11}
                                        placeholder={getLocaleContent(locale, 'page.booking-form.phone-placeholder')}
                                        style={{
                                            ...(errors.phone ? {borderColor: '#ff4d4f'} : {}),
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                        }}
                                    />
                                </FormField>
                            </div>
                        </div>

                        <div className="form-grid">
                            <div>
                                <label>
                                    {getLocaleContent(locale, 'page.booking-form.hospital')} <span>*</span>
                                </label>
                                <FormField error={errors.hospital} preserveStyle>
                                    <select
                                        className="select-hospital"
                                        name="hospital"
                                        value={form.hospital}
                                        onChange={handleChange}
                                        style={{
                                            ...(errors.hospital ? {borderColor: '#ff4d4f'} : {}),
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                        }}>
                                        <option value="">
                                            {getLocaleContent(locale, 'page.booking-form.choose-hospital')}
                                        </option>
                                        {businessData?.map(business => (
                                            <option key={business.business_id} value={business.business_id}>
                                                {business.business_name}
                                            </option>
                                        ))}
                                    </select>
                                </FormField>
                            </div>
                            {/* <div>
                                <label>{getLocaleContent(locale, 'page.booking-form.department')}</label>
                                <FormField preserveStyle>
                                    <select
                                        className="select-hospital"
                                        name="department"
                                        value={form.department}
                                        onChange={handleChange}
                                        style={{
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                        }}>
                                        <option value="">
                                            {getLocaleContent(locale, 'page.booking-form.choose-department')}
                                        </option>
                                        {departments?.map(department => (
                                            <option key={department.department_id} value={department.department_id}>
                                                {department.department_name}
                                            </option>
                                        ))}
                                    </select>
                                </FormField>
                            </div> */}

                            <div>
                                <label>{getLocaleContent(locale, 'page.booking-form.expertise')}</label>
                                <FormField preserveStyle>
                                    <select
                                        className="select-hospital"
                                        name="expertises"
                                        value={form.expertises}
                                        onChange={handleChange}
                                        style={{
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                        }}>
                                        <option value="">
                                            {getLocaleContent(locale, 'page.booking-form.choose-expertise')}
                                        </option>
                                        {expertises.map(expertise => (
                                            <option key={expertise.expertise_id} value={expertise.expertise_id}>
                                                {expertise.expertise_name}
                                            </option>
                                        ))}
                                    </select>
                                </FormField>
                            </div>
                        </div>

                        <div className="form-grid">
                            <div>
                                <label>{getLocaleContent(locale, 'page.booking-form.doctor')}</label>
                                <FormField preserveStyle>
                                    <select
                                        className="select-hospital"
                                        name="doctor"
                                        value={form.doctor}
                                        onChange={handleChange}
                                        disabled={hasAllDoctorInfo}
                                        style={{
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                            ...(hasAllDoctorInfo ? {opacity: 0.6, cursor: 'not-allowed'} : {}),
                                        }}>
                                        <option value="">
                                            {getLocaleContent(locale, 'page.booking-form.choose-doctor')}
                                        </option>
                                        {availableDoctors?.map(doctor => (
                                            <option key={doctor.user_id} value={doctor.user_id}>
                                                {doctor.full_name}
                                            </option>
                                        ))}
                                    </select>
                                </FormField>
                            </div>
                            <div>
                                <label>
                                    {getLocaleContent(locale, 'page.booking-form.time')} <span>*</span>
                                </label>
                                <FormField error={errors.time} preserveStyle>
                                    <input
                                        className="register-date"
                                        type="datetime-local"
                                        name="time"
                                        value={form.time}
                                        onChange={handleChange}
                                        onKeyDown={handleTimeKeyDown}
                                        onFocus={handleTimePicker}
                                        onClick={handleTimePicker}
                                        min={moment().format('YYYY-MM-DDTHH:mm')}
                                        style={{
                                            ...(errors.time ? {borderColor: '#ff4d4f'} : {}),
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                        }}
                                    />
                                </FormField>
                            </div>
                        </div>

                        <div>
                            <label>{getLocaleContent(locale, 'page.booking-form.symptoms-label')}</label>
                            <FormField error={errors.symptoms} preserveStyle>
                                <textarea
                                    name="symptoms"
                                    value={form.symptoms}
                                    onChange={handleChange}
                                    placeholder={getLocaleContent(locale, 'page.booking-form.symptoms-placeholder')}
                                    style={{
                                        ...(errors.symptoms ? {borderColor: '#ff4d4f'} : {}),
                                        fontFamily: 'inherit',
                                        fontSize: 'inherit',
                                    }}
                                />
                            </FormField>
                        </div>

                        <button type="submit" className="submit-btn">
                            {getLocaleContent(locale, 'page.booking-form.send')} →
                        </button>
                    </form>
                </div>

                <div className="hospital-area">
                    {hospitalsWithMap.map((h, i) => (
                        <div className="hospital-card-register" key={h.business_id}>
                            <h3>
                                <span>{String(i + 1).padStart(2, '0')}</span> {h.business_name}
                            </h3>
                            <div
                                className="hospital-info-area"
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    setSelectedMapUrl(h.mapUrl);
                                    setOpen(true);
                                }}>
                                <img className="image--register" src="/bw_image/ic_location.svg" alt="address" />
                                <p>{h.address}</p>
                            </div>
                            {h.business_phone_number && (
                                <div className="hospital-info-area">
                                    <img className="image--register" src="/bw_image/ic_call.svg" alt="phone" />
                                    <a href={`tel:${h.business_phone_number}`} onClick={e => e.stopPropagation()}>
                                        {h.business_phone_number}
                                    </a>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal thành công */}
            <BookingSuccess
                open={showSuccess}
                bookingCode={bookingCode}
                onClose={() => {
                    setShowSuccess(false);
                    setBookingCode('');
                }}
            />

            <GoogleMapView mapUrl={selectedMapUrl} open={open} onClose={() => setOpen(false)} />
        </div>
    );
}
